import { Injectable, computed, signal } from '@angular/core';
import type { SnackbarInput, SnackbarMessage } from '../models/snackbar-message.model';

@Injectable({
  providedIn: 'root',
})
export class SnackbarStore {
  private static readonly MAX_VISIBLE_ITEMS = 4;

  private readonly messagesSignal = signal<SnackbarMessage[]>([]);
  private readonly timers = new Map<string, number>();

  readonly messages = computed(() => this.messagesSignal());

  enqueue(input: SnackbarInput): SnackbarMessage {
    const next: SnackbarMessage = {
      id: input.id?.trim() || this.createId(),
      title: input.title.trim(),
      description: input.description.trim(),
      imageUrl: input.imageUrl?.trim() || null,
      createdAt: new Date().toISOString(),
      durationMs: this.normalizeDuration(input.durationMs),
      variant: input.variant ?? 'info',
      dismissible: input.dismissible ?? true,
    };

    this.clearTimer(next.id);
    this.messagesSignal.update((current) =>
      [next, ...current.filter((item) => item.id !== next.id)].slice(
        0,
        SnackbarStore.MAX_VISIBLE_ITEMS
      )
    );
    this.scheduleDismiss(next);

    return next;
  }

  dismiss(id: string): void {
    this.clearTimer(id);
    this.messagesSignal.update((current) =>
      current.filter((item) => item.id !== id)
    );
  }

  clear(): void {
    for (const id of this.timers.keys()) {
      this.clearTimer(id);
    }

    this.messagesSignal.set([]);
  }

  private scheduleDismiss(message: SnackbarMessage): void {
    if (
      typeof window === 'undefined' ||
      message.durationMs === null ||
      message.durationMs <= 0
    ) {
      return;
    }

    const timerId = window.setTimeout(() => {
      this.dismiss(message.id);
    }, message.durationMs);

    this.timers.set(message.id, timerId);
  }

  private clearTimer(id: string): void {
    const timerId = this.timers.get(id);

    if (timerId !== undefined && typeof window !== 'undefined') {
      window.clearTimeout(timerId);
    }

    this.timers.delete(id);
  }

  private normalizeDuration(durationMs: number | null | undefined): number | null {
    if (durationMs === null) {
      return null;
    }

    if (typeof durationMs !== 'number' || Number.isNaN(durationMs)) {
      return 6000;
    }

    return durationMs > 0 ? Math.round(durationMs) : null;
  }

  private createId(): string {
    if (
      typeof globalThis.crypto !== 'undefined' &&
      'randomUUID' in globalThis.crypto
    ) {
      return globalThis.crypto.randomUUID();
    }

    return `snackbar-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  }
}
